import React from 'react';
import cx from 'classnames';
import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';

import { palette } from '~/utils/colours';
import { createFontAwesomeIcon } from '~/App/Components/Attributes/util';
import ReplaceableComponents from '~/components/higher-order/ReplaceableComponents';

const IconBeforeContainer = styled.span``;

const ItemChildrenContainer = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ListItemBasic = styled.li`
  display: list-item;
  padding: ${(props) => props.theme.padding};
  font-size: 1.1em;
  font-weight: ${(props) => props.theme.fontWeight};
  height: 36px;
  cursor: ${(props) => {
    if (props.disabled) {
      return 'not-allowed';
    }

    return (props.onClick || props.href) != null ? 'pointer' : null;
  }};

  &:hover {
    text-decoration: none;
  }

  &:focus {
    text-decoration: none;
  }

  & ${IconBeforeContainer} {
    position: absolute;
    left: 10px;
  }

  & > :nth-child(2) {
    margin-left: 20px;
  }

  &.active {
    color: ${palette.tealishTwo};
  }
`;

const Link = ListItemBasic.withComponent('a');

Link.defaultProps = {
  target: '_blank',
};

export const components = {
  Link,
  Item: ListItemBasic,
};

type ListItemProps = {
  href?: string;
  active?: boolean;
  disabled?: boolean;
  target?: string;
  itemStyle?: string;
  theme: Record<string, unknown>;
  iconBefore?: Record<string, unknown>;
  children?: React.ReactNode;
  components?: React.ElementType;
  className?: string;
  onClick?(): void;
};

@ReplaceableComponents(components)
class ListItem extends React.Component<ListItemProps> {
  render() {
    const { components, children, className, ...props } = this.props;
    const { Item, Link } = components;
    const theme = this.getTheme();

    const ItemComponent = this.props.href != null ? Link : Item;

    return (
      <ItemComponent
        className={cx(className, ['bg-white hover:bg-neutral-50'], {
          active: props.active,
        })}
        {...props}
        theme={theme}
      >
        {this.renderIconBefore()}
        <ItemChildrenContainer>{children}</ItemChildrenContainer>
      </ItemComponent>
    );
  }

  renderIconBefore() {
    const { iconBefore } = this.props;

    if (iconBefore == null) {
      return null;
    }

    return (
      <IconBeforeContainer>
        {React.createElement(createFontAwesomeIcon(iconBefore))}
      </IconBeforeContainer>
    );
  }

  getItemStyle() {
    const { disabled, itemStyle } = this.props;

    if (itemStyle) {
      return itemStyle;
    }

    if (disabled) {
      return 'disabled';
    }

    return null;
  }

  getTheme() {
    const { theme } = this.props;
    const itemStyle = this.getItemStyle();

    switch (itemStyle) {
      case 'danger':
        return theme.itemDanger;
      case 'disabled':
        return theme.itemDisabled;
      default:
        return theme.item;
    }
  }
}

export default withTheme(ListItem);
